import { type PayRun, PayRunCycleLogStatus } from '@core/generated/gql-types';
import type { PayRunCycleLogWithReferences } from '@core/payroll/payroll.type';
import {
  EMPTY_CELL_LABEL,
  formatCents,
  formatDateRangeLabel,
  formatPersonName,
  isHttpUrl,
  MS_PER_DAY,
} from '@/modules/payroll/utils/payrollFormat.utils';

export const CYCLE_STATUS_FILTERS: {
  key: PayRunCycleLogStatus | 'ALL';
  label: string;
}[] = [
  { key: 'ALL', label: 'All cycles' },
  { key: PayRunCycleLogStatus.Processing, label: 'Processing' },
  { key: PayRunCycleLogStatus.Completed, label: 'Completed' },
  {
    key: PayRunCycleLogStatus.CompletedWithErrors,
    label: 'Completed w/ errors',
  },
  { key: PayRunCycleLogStatus.Failed, label: 'Failed' },
];

/** A titled table rendered in the payroll table modal. */
export type PayrollTableData = {
  title: string;
  subtitle: string;
  columns: string[];
  rows: string[][];
};

const formatMarginCents = (billableCents: number, payableCents: number) =>
  formatCents(billableCents - payableCents);

const formatPayRunWorker = (run: PayRun) =>
  formatPersonName(run.user, run.userId);

export const getCyclePeriodLengthDays = (
  cycle: PayRunCycleLogWithReferences,
): number => {
  if (cycle.periodLengthInDays > 0) {
    return cycle.periodLengthInDays;
  }
  const startMs = Date.parse(cycle.periodStartISO);
  const endMs = Date.parse(cycle.periodEndISO);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return 0;
  }
  return Math.max(1, Math.round((endMs - startMs) / MS_PER_DAY) + 1);
};

export const sortCyclesNewestFirst = (
  cycles: PayRunCycleLogWithReferences[],
): PayRunCycleLogWithReferences[] =>
  [...cycles].sort((a, b) => {
    const aDate = Date.parse(a.createdAt);
    const bDate = Date.parse(b.createdAt);
    return Number.isNaN(aDate) || Number.isNaN(bDate) ? 0 : bDate - aDate;
  });

export const getCycleCoverage = (cycle: PayRunCycleLogWithReferences) => {
  const workerIds = new Set<string>();
  const clientIds = new Set<string>();
  const jobIds = new Set<string>();
  let daySequenceCount = 0;
  cycle.payRuns.forEach((run) => {
    if (run.userId) {
      workerIds.add(run.userId);
    }
    if (run.client?.id) {
      clientIds.add(run.client.id);
    }
    run.payRunItems.forEach((item) => {
      if (!item) {
        return;
      }
      daySequenceCount += 1;
      if (item.clientId) {
        clientIds.add(item.clientId);
      }
      if (item.jobId) {
        jobIds.add(item.jobId);
      }
    });
  });
  return {
    workerCount: workerIds.size || cycle.payRunCount || 0,
    clientCount: clientIds.size,
    jobCount: jobIds.size,
    daySequenceCount,
  };
};

/** Every pay run item in the cycle, one row per day sequence. */
export const buildCycleDaySequencesTable = (
  cycle: PayRunCycleLogWithReferences,
): PayrollTableData => {
  const rows: { worker: string; cells: string[] }[] = [];
  cycle.payRuns.forEach((run) => {
    run.payRunItems.forEach((item) => {
      if (!item) {
        return;
      }
      const worker = formatPayRunWorker(run);
      const jobTitle =
        item.job?.title ?? `Job ${item.jobId ?? EMPTY_CELL_LABEL}`;
      const classification = item.job?.classification?.name;
      rows.push({
        worker,
        cells: [
          worker,
          classification ? `${classification} • ${jobTitle}` : jobTitle,
          item.client?.name ??
            run.client?.name ??
            item.clientId ??
            EMPTY_CELL_LABEL,
          formatDateRangeLabel(
            item.dateISO ?? item.checkInTime ?? run.payRunDateFromISO,
            item.checkOutTime,
          ),
          (item.hoursWorked ?? 0).toFixed(2),
          formatCents(item.totalPayableAmountInCents ?? 0),
          formatCents(item.clientBillableAmountInCents ?? 0),
          formatMarginCents(
            item.clientBillableAmountInCents ?? 0,
            item.totalPayableAmountInCents ?? 0,
          ),
        ],
      });
    });
  });
  rows.sort((a, b) => a.worker.localeCompare(b.worker));
  return {
    title: 'Day sequences',
    subtitle: `${rows.length} day sequences`,
    columns: [
      'Worker',
      'Job',
      'Client',
      'Window',
      'Hours',
      'Payable',
      'Billable',
      'Margin',
    ],
    rows: rows.map((row) => row.cells),
  };
};

type Totals = {
  hours: number;
  payableCents: number;
  billableCents: number;
};

const formatTotals = (totals: Totals) => [
  totals.hours.toFixed(2),
  formatCents(totals.payableCents),
  formatCents(totals.billableCents),
  formatMarginCents(totals.billableCents, totals.payableCents),
];

export const buildCycleWorkersTable = (
  cycle: PayRunCycleLogWithReferences,
): PayrollTableData => {
  const workers = new Map<
    string,
    Totals & {
      name: string;
      payRuns: number;
      jobIds: Set<string>;
      clientIds: Set<string>;
    }
  >();
  cycle.payRuns.forEach((run) => {
    const key = run.userId ?? run.id;
    const entry = workers.get(key) ?? {
      name: formatPayRunWorker(run),
      payRuns: 0,
      hours: 0,
      payableCents: 0,
      billableCents: 0,
      jobIds: new Set<string>(),
      clientIds: new Set<string>(),
    };
    entry.payRuns += 1;
    entry.hours += run.totalHours ?? 0;
    entry.payableCents += run.payableAmountCents ?? 0;
    if (run.client?.id) {
      entry.clientIds.add(run.client.id);
    }
    run.payRunItems.forEach((item) => {
      if (!item) {
        return;
      }
      entry.billableCents += item.clientBillableAmountInCents ?? 0;
      if (item.jobId) {
        entry.jobIds.add(item.jobId);
      }
      if (item.clientId) {
        entry.clientIds.add(item.clientId);
      }
    });
    workers.set(key, entry);
  });
  const rows = Array.from(workers.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((entry) => [
      entry.name,
      `${entry.payRuns}`,
      `${entry.jobIds.size}`,
      `${entry.clientIds.size}`,
      ...formatTotals(entry),
    ]);
  return {
    title: 'Workers',
    subtitle: `${rows.length} workers`,
    columns: [
      'Worker',
      'Pay runs',
      'Jobs',
      'Clients',
      'Hours',
      'Payable',
      'Billable',
      'Margin',
    ],
    rows,
  };
};

export const buildCycleJobsTable = (
  cycle: PayRunCycleLogWithReferences,
): PayrollTableData => {
  const jobs = new Map<
    string,
    Totals & { label: string; workerIds: Set<string>; clientIds: Set<string> }
  >();
  cycle.payRuns.forEach((run) => {
    run.payRunItems.forEach((item) => {
      if (!item?.jobId) {
        return;
      }
      const entry = jobs.get(item.jobId) ?? {
        label: item.job?.title ?? `Job ${item.jobId}`,
        workerIds: new Set<string>(),
        clientIds: new Set<string>(),
        hours: 0,
        payableCents: 0,
        billableCents: 0,
      };
      if (run.userId) {
        entry.workerIds.add(run.userId);
      }
      if (item.clientId) {
        entry.clientIds.add(item.clientId);
      }
      if (run.client?.id) {
        entry.clientIds.add(run.client.id);
      }
      entry.hours += item.hoursWorked ?? 0;
      entry.payableCents += item.totalPayableAmountInCents ?? 0;
      entry.billableCents += item.clientBillableAmountInCents ?? 0;
      jobs.set(item.jobId, entry);
    });
  });
  const rows = Array.from(jobs.entries())
    .sort((a, b) => a[1].label.localeCompare(b[1].label))
    .map(([jobId, entry]) => [
      entry.label,
      jobId,
      `${entry.workerIds.size}`,
      `${entry.clientIds.size}`,
      ...formatTotals(entry),
    ]);
  return {
    title: 'Jobs',
    subtitle: `${rows.length} jobs`,
    columns: [
      'Job',
      'Job ID',
      'Workers',
      'Clients',
      'Hours',
      'Payable',
      'Billable',
      'Margin',
    ],
    rows,
  };
};

export const buildCycleClientsTable = (
  cycle: PayRunCycleLogWithReferences,
): PayrollTableData => {
  const clients = new Map<
    string,
    Totals & { label: string; workerIds: Set<string>; jobIds: Set<string> }
  >();
  cycle.payRuns.forEach((run) => {
    run.payRunItems.forEach((item) => {
      if (!item) {
        return;
      }
      const clientId = item.clientId ?? run.client?.id ?? 'unknown-client';
      const entry = clients.get(clientId) ?? {
        label: item.client?.name ?? run.client?.name ?? `Client ${clientId}`,
        workerIds: new Set<string>(),
        jobIds: new Set<string>(),
        hours: 0,
        payableCents: 0,
        billableCents: 0,
      };
      if (run.userId) {
        entry.workerIds.add(run.userId);
      }
      if (item.jobId) {
        entry.jobIds.add(item.jobId);
      }
      entry.hours += item.hoursWorked ?? 0;
      entry.payableCents += item.totalPayableAmountInCents ?? 0;
      entry.billableCents += item.clientBillableAmountInCents ?? 0;
      clients.set(clientId, entry);
    });
  });
  const rows = Array.from(clients.entries())
    .sort((a, b) => a[1].label.localeCompare(b[1].label))
    .map(([clientId, entry]) => [
      entry.label,
      clientId,
      `${entry.workerIds.size}`,
      `${entry.jobIds.size}`,
      ...formatTotals(entry),
    ]);
  return {
    title: 'Clients',
    subtitle: `${rows.length} clients`,
    columns: [
      'Client',
      'Client ID',
      'Workers',
      'Jobs',
      'Hours',
      'Payable',
      'Billable',
      'Margin',
    ],
    rows,
  };
};

export type IntegrationProvider = 'keypay' | 'astute';

export type IntegrationSyncSummary = {
  provider: IntegrationProvider;
  label: string;
  lastSyncedAt: string | number | null;
  totalsLabel: string | null;
  reportUrl: string | null;
  payloadUrl: string | null;
};

const INTEGRATION_LABELS: Record<IntegrationProvider, string> = {
  keypay: 'Keypay',
  astute: 'Astute',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getReference = (
  references: Record<string, unknown> | null | undefined,
  key: string,
): Record<string, unknown> | null => {
  const entry = references?.[key];
  return isRecord(entry) ? entry : null;
};

const asString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const formatTotalsSummary = (totals: unknown): string | null => {
  if (!isRecord(totals)) {
    return null;
  }
  const entries = Object.entries(totals)
    .filter(
      ([, value]) => typeof value === 'number' || typeof value === 'string',
    )
    .map(([key, value]) => `${key}: ${value}`);
  return entries.length ? entries.join(' · ') : null;
};

/**
 * Sync metadata per payroll integration, read from the cycle log's
 * `external.<provider>.pay_run_cycle.*` references. Providers with no data
 * are omitted.
 */
export const getIntegrationSyncSummaries = (
  references: Record<string, unknown> | null | undefined,
): IntegrationSyncSummary[] =>
  (Object.keys(INTEGRATION_LABELS) as IntegrationProvider[])
    .map((provider) => {
      const summary = getReference(
        references,
        `external.${provider}.pay_run_cycle.summary`,
      );
      const payload = getReference(
        references,
        `external.${provider}.pay_run_cycle.sent_payload`,
      );
      const reportKey = asString(summary?.reportKey);
      const payloadKey = asString(payload?.key);
      const lastSyncedAt =
        typeof summary?.lastSyncedAt === 'string' ||
        typeof summary?.lastSyncedAt === 'number'
          ? summary.lastSyncedAt
          : null;
      return {
        provider,
        label: INTEGRATION_LABELS[provider],
        lastSyncedAt,
        totalsLabel: formatTotalsSummary(summary?.totals),
        reportUrl:
          asString(summary?.signedUrl) ??
          (isHttpUrl(reportKey) ? reportKey : null),
        payloadUrl:
          asString(payload?.signedUrl) ??
          (isHttpUrl(payloadKey) ? payloadKey : null),
      };
    })
    .filter(
      (entry) =>
        entry.lastSyncedAt !== null ||
        entry.totalsLabel !== null ||
        entry.reportUrl !== null ||
        entry.payloadUrl !== null,
    );

const PAYLOAD_TABLE_COLUMNS = [
  'Batch',
  'Employee',
  'Start',
  'End',
  'Breaks',
  'Rate',
  'Status',
  'External ID',
  'Comments',
  'From',
  'To',
  'Approved',
  'Replace',
];

const formatPayloadValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return EMPTY_CELL_LABEL;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? `${value}` : EMPTY_CELL_LABEL;
  }
  return `${value}`;
};

/** Parses `DD/MM/YYYY hh:mm[:ss] [AM|PM]`, as sent to the payroll provider. */
const parseDayFirstDateTime = (value: string): Date | null => {
  const [datePart, ...timeParts] = value.split(' ');
  const [day, month, year] = (datePart ?? '').split('/').map(Number);
  if (!day || !month || !year) {
    return null;
  }
  const [time = '', meridiemRaw = ''] = timeParts;
  const [hours = NaN, minutes = NaN, seconds = 0] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  const meridiem = meridiemRaw.toUpperCase();
  let hour24 = hours;
  if (meridiem === 'PM' && hours < 12) {
    hour24 += 12;
  } else if (meridiem === 'AM' && hours === 12) {
    hour24 = 0;
  }
  return new Date(year, month - 1, day, hour24, minutes, seconds);
};

const parsePayloadDate = (value: unknown): Date | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value > 1e9 && value < 1e12 ? value * 1000 : value);
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && numeric > 1e9) {
    return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
  }
  if (value.includes('/') && value.includes(' ')) {
    const date = parseDayFirstDateTime(value);
    if (date && !Number.isNaN(date.valueOf())) {
      return date;
    }
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

const formatPayloadDate = (value: unknown): string => {
  const date = parsePayloadDate(value);
  return date
    ? date.toLocaleString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : formatPayloadValue(value);
};

const formatPayloadBreaks = (value: unknown): string => {
  if (!Array.isArray(value)) {
    return EMPTY_CELL_LABEL;
  }
  const labels = value
    .filter(isRecord)
    .map((entry) => {
      const start = formatPayloadDate(entry.startTime);
      const end = formatPayloadDate(entry.endTime);
      if (start !== EMPTY_CELL_LABEL && end !== EMPTY_CELL_LABEL) {
        return `${start} -> ${end}`;
      }
      return start !== EMPTY_CELL_LABEL ? start : end;
    })
    .filter((label) => label !== EMPTY_CELL_LABEL);
  return labels.length ? labels.join(', ') : EMPTY_CELL_LABEL;
};

/**
 * Flattens a payroll provider "sent payload" (timesheet batches keyed by
 * employee) into table rows. Returns null when there are no timesheets.
 */
export const buildPayloadTable = (
  payload: unknown,
): Pick<PayrollTableData, 'columns' | 'rows'> | null => {
  if (!isRecord(payload) || !Array.isArray(payload.timesheetBatches)) {
    return null;
  }
  const rows: string[][] = [];
  payload.timesheetBatches.filter(isRecord).forEach((batch) => {
    const batchPayload = isRecord(batch.payload) ? batch.payload : null;
    const timesheets =
      batchPayload && isRecord(batchPayload.timesheets)
        ? batchPayload.timesheets
        : null;
    if (!batchPayload || !timesheets) {
      return;
    }
    Object.entries(timesheets).forEach(([employeeKey, entries]) => {
      if (!Array.isArray(entries)) {
        return;
      }
      entries.filter(isRecord).forEach((entry) => {
        const employeeId =
          typeof entry.employeeId === 'string' ||
          typeof entry.employeeId === 'number'
            ? entry.employeeId
            : employeeKey;
        rows.push([
          formatPayloadValue(batch.batchIndex),
          formatPayloadValue(employeeId),
          formatPayloadDate(entry.startTime),
          formatPayloadDate(entry.endTime),
          formatPayloadBreaks(entry.breaks),
          formatPayloadValue(entry.rate),
          formatPayloadValue(entry.status),
          formatPayloadValue(entry.externalId),
          formatPayloadValue(entry.comments),
          formatPayloadDate(batchPayload.fromDate),
          formatPayloadDate(batchPayload.toDate),
          formatPayloadValue(batchPayload.approved),
          formatPayloadValue(batchPayload.replaceExisting),
        ]);
      });
    });
  });
  return rows.length ? { columns: PAYLOAD_TABLE_COLUMNS, rows } : null;
};
